// src/db/models/userModel.js
import { api } from '../../api/client';

export const UserModel = {
  async create(userData) {
    return api.createUser(userData);
  },

  async findByEmailAndPin(email, pinCode) {
    return api.verifyUser(email, pinCode);
  },

  async findByAccessCode(accessCode) {
    return api.verifyAccessCode(accessCode);
  },

  async findAll() {
    return api.getAllUsers();
  },

  async delete(id) {
    return api.deleteUser(id);
  },

  async update(id, userData) {
    try {
      const userResponse = await api.getAllUsers();
      const user = userResponse.find(u => u.id === id);
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const isMainUser = ['teacher', 'main_user'].includes(user.role);
      
      const updatedData = { ...userData };
      
      if (isMainUser && updatedData.maxSubUsers !== undefined) {
        const maxSubUsers = parseInt(updatedData.maxSubUsers);
        if (isNaN(maxSubUsers) || maxSubUsers < 0) {
          //console.warn(`Invalid maxSubUsers value: ${updatedData.maxSubUsers}, using default or current value`);
          updatedData.maxSubUsers = user.maxSubUsers || user.max_subusers || 5;
        } else {
          updatedData.maxSubUsers = maxSubUsers;
        }
      }
      
      const updatedUser = await api.updateUser(id, updatedData);
      
      if (isMainUser) {
        const settingsToPropagate = {};
        
        if (userData.hasOwnProperty('isEnabled')) {
          settingsToPropagate.isEnabled = userData.isEnabled;
        }
        
        if (userData.hasOwnProperty('restrictTime')) {
          settingsToPropagate.restrictTime = userData.restrictTime;
          settingsToPropagate.accessStartTime = userData.accessStartTime;
          settingsToPropagate.accessEndTime = userData.accessEndTime;
        }
        
        if (userData.hasOwnProperty('restrictDays')) {
          settingsToPropagate.restrictDays = userData.restrictDays;
          settingsToPropagate.daysAllowed = userData.daysAllowed;
        }
        
        if (Object.keys(settingsToPropagate).length > 0) {
          try {
            const allUsers = await api.getAllUsers();
            const subUsers = allUsers.filter(u => 
              u.role === 'subuser' &&
              Number(u.parentUserId ?? u.parent_user_id) === Number(user.id)
            );
            
            for (const subUser of subUsers) {
              await api.patchUser(subUser.id, settingsToPropagate);
            }
            
          } catch (err) {
            console.error('Error propagating settings to subusers:', err);
          }
        }
      }
      
      return updatedUser;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },
  
  async updateStatus(id, isEnabled) {
    return api.updateUserStatus(id, isEnabled);
  },
  
  async updateValidity(id, validityDays) {
    return api.updateUserValidity(id, validityDays);
  },

  async updateTimeRestriction(id, timeRestrictionData) {
    return api.updateUser(id, timeRestrictionData);
  },
  
  /**
   * Find expired and disabled users
   * @returns {Promise<Array>} Array of expired user objects
   */
  async findExpiredUsers() {
    try {
      const users = await this.findAll();
      const currentDate = new Date();
      
      return users.filter(user => {
        // Only consider disabled users
        if (user.isEnabled || user.is_enabled) return false;
        
        // Calculate expiration date
        const assignmentDate = new Date(user.dateAssigned || user.date_assigned);
        const validityDays = user.validityDays || user.validity_days || 300;
        const expirationDate = new Date(assignmentDate);
        expirationDate.setDate(expirationDate.getDate() + parseInt(validityDays));
        
        // User is expired if current date is past expiration date
        return currentDate > expirationDate;
      });
    } catch (error) {
      console.error('Error finding expired users:', error);
      throw error;
    }
  },
  
  /**
   * Clean up users safely - removes expired, disabled users that don't have dependent records
   * This implementation maintains compatibility with the existing API structure
   * @returns {Promise<Object>} Results of the cleanup operation
   */
  async cleanupExpiredUsers() {
    try {
      
      // First get all expired users
      const expiredUsers = await this.findExpiredUsers();
      
      if (expiredUsers.length === 0) {
        return {
          success: true,
          message: 'No expired users found for cleanup',
          cleanedCount: 0
        };
      }
      
      // Get performances to check for dependencies
      const performancesResponse = await fetch('/api/performances');
      if (!performancesResponse.ok) {
        throw new Error(`Failed to fetch performances: ${performancesResponse.status}`);
      }
      const performances = await performancesResponse.json();
      
      // Get all users to check for subuser relationships
      const allUsers = await this.findAll();
      
      // Create sets for quick lookups
      const accessCodesWithPerformances = new Set(
        performances.map(p => p.code_used || p.codeUsed)
      );
      
      // Parentage is an explicit database relationship; punctuation in an
      // access code is never account ownership.
      const parentUserIds = new Set();
      allUsers.forEach(user => {
        const parentId = Number(user.parentUserId ?? user.parent_user_id);
        if (user.role === 'subuser' && Number.isInteger(parentId) && parentId > 0) parentUserIds.add(parentId);
      });
      
      // Filter users that are safe to delete
      const safeToDeleteUsers = expiredUsers.filter(user => {
        const accessCode = user.accessCode || user.access_code;
        
        // Don't delete users with performance records
        if (accessCodesWithPerformances.has(accessCode)) {
          return false;
        }
        
        // Don't delete main users with active subusers
        if (parentUserIds.has(Number(user.id))) {
          return false;
        }
        
        return true;
      });
      
      // Get all settings to check for user relationships
      const settingsResponse = await fetch('/api/assessment-settings');
      if (!settingsResponse.ok) {
        throw new Error(`Failed to fetch assessment settings: ${settingsResponse.status}`);
      }
      const settings = await settingsResponse.json();
      
      // Delete users and related settings
      const deleteResults = [];
      for (const user of safeToDeleteUsers) {
        try {
          const userId = user.id;
          
          // First, find and delete all settings for this user
          const userSettings = settings.filter(s => s.user_id === userId);
          
          for (const setting of userSettings) {
            const settingDeleteResponse = await fetch(`/api/assessment-settings/${setting.id}`, {
              method: 'DELETE'
            });
            
            if (!settingDeleteResponse.ok) {
              console.warn(`Failed to delete setting ${setting.id} for user ${userId}: ${settingDeleteResponse.status}`);
            }
          }
          
          // Now delete the user using our existing API method
          await api.deleteUser(userId);
          
          deleteResults.push({
            success: true,
            userId,
            email: user.email,
            accessCode: user.accessCode || user.access_code,
            settingsDeleted: userSettings.length
          });
        } catch (error) {
          console.error(`Error deleting user ${user.id}:`, error);
          deleteResults.push({
            success: false,
            userId: user.id,
            email: user.email,
            accessCode: user.accessCode || user.access_code,
            error: error.message
          });
        }
      }
      
      const successfulDeletes = deleteResults.filter(result => result.success);
      
      return {
        success: true,
        message: `Successfully cleaned up ${successfulDeletes.length} of ${safeToDeleteUsers.length} expired users`,
        cleanedCount: successfulDeletes.length,
        totalExpiredCount: expiredUsers.length,
        safeDeletionCount: safeToDeleteUsers.length,
        deletionResults: deleteResults,
        performancesPreserved: performances.length,
        settingsPreserved: settings.length - successfulDeletes.reduce((acc, cur) => acc + (cur.settingsDeleted || 0), 0)
      };
    } catch (error) {
      console.error('Error cleaning up expired users:', error);
      return {
        success: false,
        message: `Cleanup failed: ${error.message}`,
        error
      };
    }
  }
};
